import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline';

const INSTALL_PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed';
const INSTALL_PROMPT_DELAY_MS = 5000; // Wait 5 seconds after login before showing

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    if (dismissed) return;

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show prompt after delay
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, INSTALL_PROMPT_DELAY_MS);

      return () => clearTimeout(timer);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);
    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'installed');
      } else {
        localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'dismissed');
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'dismissed');
    setShowPrompt(false);
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={handleDismiss}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 50 }}
            className="modal-content max-w-md"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <DevicePhoneMobileIcon className="w-8 h-8 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-white mb-2">
                  Install MyFamily App
                </h2>
                <p className="text-sm text-surface-400">
                  Install MyFamily as an app for a better experience. Get faster access, offline support, and a native app feel.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="btn-icon"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                className="btn-secondary flex-1"
              >
                Later
              </button>
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                className="btn-primary flex-1"
              >
                {isInstalling ? 'Installing...' : 'Install'}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
